import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { Menu } from "@headlessui/react";
import { HiOutlinePlus } from "react-icons/hi2";
import {
    useFetchNotificationRulesMutation,
    useUpdateNotificationRuleStatusMutation,
} from "../../../features/tenant/notificationRule/NotificationRuleApi";
import { useGetAllNotificationTemplatesMutation } from "../../../features/tenant/notificationTemplate/NotificationTemplateApi";
import toast from "react-hot-toast";
import { getVendorName } from "../../../utils/getVendorName";

const NotificationRule = () => {
    const navigate = useNavigate();
    const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme")).theme.color_shades;

    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationResetToggle, setPaginationResetToggle] = useState(false);
    const [ruleList, setRuleList] = useState([]);
    const [totalRows, setTotalRows] = useState(0);

    // Filters
    const [filterNotificationId, setFilterNotificationId] = useState("");
    const [filterRuleType, setFilterRuleType] = useState("");
    const [filterActive, setFilterActive] = useState("");

    // Notification list for dropdown
    const [notificationList, setNotificationList] = useState([]);

    const [
        FetchNotificationRules,
        {
            data: FetchNotificationRulesData,
            isLoading: FetchNotificationRulesIsLoading,
            isError: FetchNotificationRulesIsError,
            error: FetchNotificationRulesError,
        },
    ] = useFetchNotificationRulesMutation();

    const [
        UpdateNotificationRuleStatus,
        {
            data: UpdateNotificationRuleStatusData,
            isLoading: UpdateNotificationRuleStatusIsLoading,
            isError: UpdateNotificationRuleStatusIsError,
            error: UpdateNotificationRuleStatusError,
        },
    ] = useUpdateNotificationRuleStatusMutation();

    const [
        GetAllNotificationTemplates,
        {
            data: GetAllNotificationTemplatesData,
        },
    ] = useGetAllNotificationTemplatesMutation();

    const fetchRules = (limit, offset) => {
        const body = {
            limit: limit,
            offset: offset,
        };

        if (filterNotificationId) {
            body.notification_id = filterNotificationId;
        }
        if (filterRuleType) {
            body.rule_type = filterRuleType;
        }
        if (filterActive !== "") {
            body.active = filterActive === "1" ? true : false;
        }

        FetchNotificationRules({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            body: body,
        });
    };

    useEffect(() => {
        fetchRules(limit, offset);
    }, [limit, offset]);

    // Fetch notifications for filter dropdown
    useEffect(() => {
        GetAllNotificationTemplates({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            body: {
                limit: 1000, // Get all
                offset: 0,
            },
        });
    }, []);

    useEffect(() => {
        if (GetAllNotificationTemplatesData) {
            setNotificationList(GetAllNotificationTemplatesData.body.data || []);
        }
    }, [GetAllNotificationTemplatesData]);

    useEffect(() => {
        if (FetchNotificationRulesData) {
            toast.dismiss();
            setRuleList(FetchNotificationRulesData.body.data || []);
            setTotalRows(FetchNotificationRulesData.body.total_count || 0);
        }
        if (FetchNotificationRulesError) {
            toast.dismiss();
            toast.error("Failed to fetch notification rules");
        }
    }, [FetchNotificationRulesData, FetchNotificationRulesError]);

    const handleStatusToggle = (row) => {
        const newStatus = !row.active;
        UpdateNotificationRuleStatus({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            id: row.id,
            active: newStatus,
        });
    };

    useEffect(() => {
        if (UpdateNotificationRuleStatusData) {
            toast.dismiss();
            toast.success(UpdateNotificationRuleStatusData.message || "Status updated successfully");
            fetchRules(limit, offset);
        }
        if (UpdateNotificationRuleStatusError) {
            toast.dismiss();
            toast.error("Failed to update status");
        }
    }, [UpdateNotificationRuleStatusData, UpdateNotificationRuleStatusError]);

    const handleApplyFilters = () => {
        setOffset(0);
        setCurrentPage(1);
    setPaginationResetToggle((prev) => !prev);
        fetchRules(limit, 0);
    };

    const handleClearFilters = () => {
        setFilterNotificationId("");
        setFilterRuleType("");
        setFilterActive("");
        setOffset(0);
        setCurrentPage(1);
    setPaginationResetToggle((prev) => !prev);
        FetchNotificationRules({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            body: {
                limit: limit,
                offset: 0,
            },
        });
    };

    const columns = [
        {
            name: "S.No",
            cell: (row, idx) => offset + idx + 1,
            width: "80px",
        },
        {
            name: "Notification Name",
            cell: (row) => row.notification_type || "-",
            sortable: true,
        },
        {
            name: "Rule Type",
            cell: (row) => {
                if (row.rule_type === "EVENT") return "Event";
                if (row.rule_type === "FREQUENCY") return "Frequency";
                return row.rule_type;
            },
            sortable: true,
        },
        {
            name: "Frequency Type",
            cell: (row) => row.frequency_type || "-",
            sortable: true,
        },
        {
            name: "Frequency Value",
            cell: (row) => row.frequency_value || "-",
            sortable: true,
        },
        {
            name: "Rule Config Value",
            cell: (row) => row.rule_config?.value || "-",
            sortable: true,
        },
        {
            name: "User Type",
            cell: (row) => row.user_types.join(", ") || "-",
            sortable: true,
        },
        {
            name: "Status",
            cell: (row) =>
                row.active ? (
                    <button
                        onClick={() => handleStatusToggle(row)}
                        className="flex justify-center items-center rounded bg-green-900 px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-green-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-green-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-green-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)]"
                    >
                        Active
                    </button>
                ) : (
                    <button
                        onClick={() => handleStatusToggle(row)}
                        className="flex justify-center items-center rounded bg-gray-900 px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-gray-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-gray-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-gray-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)]"
                    >
                        InActive
                    </button>
                ),
            sortable: true,
            width: "120px",
        },
    ];

    const handlePageChange = (pageNumber) => {
        const newOffset = (pageNumber - 1) * limit;
        setOffset(newOffset);
        setCurrentPage(pageNumber);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setLimit(newPerPage);
    };

    return (
        <>
            <div className="flex w-full bg-gray-200 h-full justify-center">
                <div className="w-full">
                    <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
                        Notification Rules
                        <LinksComponent />
                    </div>
                    <div className="p-3">
                        <div className="p-3 bg-white shadow rounded-lg">
                            {/* Filters */}
                            <div className="p-3 bg-white shadow rounded-lg mb-3">
                                <div className="flex gap-3 items-end">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-900 mb-1">
                                            Notification Name
                                        </label>
                                        <select
                                            className="h-10 shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                                            value={filterNotificationId}
                                            onChange={(e) => setFilterNotificationId(e.target.value)}
                                        >
                                            <option value="">All</option>
                                            {notificationList.map((notification) => (
                                                <option key={notification.id} value={notification.id}>
                                                    {notification.notification_type}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-900 mb-1">
                                            Rule Type
                                        </label>
                                        <select
                                            className="h-10 shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                                            value={filterRuleType}
                                            onChange={(e) => setFilterRuleType(e.target.value)}
                                        >
                                            <option value="">All</option>
                                            <option value="EVENT">Event</option>
                                            <option value="FREQUENCY">Frequency</option>
                                        </select>
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-900 mb-1">
                                            Active Status
                                        </label>
                                        <select
                                            className="h-10 shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                                            value={filterActive}
                                            onChange={(e) => setFilterActive(e.target.value)}
                                        >
                                            <option value="">All</option>
                                            <option value="1">Active</option>
                                            <option value="0">Inactive</option>
                                        </select>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={handleApplyFilters}
                                            className="h-10 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                                        >
                                            Search
                                        </button>
                                        <button
                                            onClick={handleClearFilters}
                                            className="h-10 text-gray-700 bg-gray-200 hover:bg-gray-300 focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <DataTableComponent
                                columns={columns}
                                data={ruleList}
                                totalRows={totalRows}
                                handlePerRowsChange={handlePerRowsChange}
                                handlePageChange={handlePageChange}
                                paginationDefaultPage={currentPage}
                                paginationResetDefaultPage={paginationResetToggle}
                            />

                            {/* Floating Add Button */}
                            <div
                                className="fixed bottom-0 right-16 w-16 h-16 mr-10 mb-2 cursor-pointer"
                                id="box_btn"
                            >
                                <div className="text-right">
                                    <Menu className="relative inline-block text-left">
                                        <div>
                                            <Menu.Button
                                                onClick={() =>
                                                    navigate(`add`, {
                                                        state: {
                                                            edit: false,
                                                        },
                                                    })
                                                }
                                                className="inline-flex justify-center w-12 h-12 rounded-full text-sm font-medium text-white hover:bg-opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
                                                style={{ backgroundColor: `${primaryColor["700"]}` }}
                                            >
                                                <HiOutlinePlus className="w-full h-full p-3" />
                                            </Menu.Button>
                                        </div>
                                    </Menu>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default NotificationRule;
